import { DeleteOutlined, HistoryOutlined } from "@ant-design/icons";
import { Flex, Space, Tag, Tooltip, Typography } from "antd";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { UnreachableCaseError } from "ts-essentials";
import { type RecipeDetailFragment } from "../../gql/generated/graphqlSchema.tsx";
import { NutritionSummary } from "../../components/NutritionSummary.tsx";
import { routes } from "../../routes.ts";
import { deletedName } from "../../styles/shared.styles.ts";

type IngredientRow = RecipeDetailFragment["ingredients"][number];

const ingredientName = (row: IngredientRow) => {
  switch (row.__typename) {
    case "IngredientRecipeIngredient":
      return row.ingredient.name;
    case "SubRecipeRecipeIngredient":
      return row.recipe.name;
    default:
      throw new UnreachableCaseError(row);
  }
};

const ingredientNameForLabel = (row: IngredientRow) => {
  switch (row.__typename) {
    case "IngredientRecipeIngredient":
      return row.ingredient.nameForLabel;
    case "SubRecipeRecipeIngredient":
      return row.recipe.nameForLabel;
    default:
      throw new UnreachableCaseError(row);
  }
};

const ingredientDeleted = (row: IngredientRow) => {
  switch (row.__typename) {
    case "IngredientRecipeIngredient":
      return row.ingredient.deleted;
    case "SubRecipeRecipeIngredient":
      return row.recipe.deleted;
    default:
      throw new UnreachableCaseError(row);
  }
};

const ingredientNutritionProfile = (row: IngredientRow) => {
  switch (row.__typename) {
    case "IngredientRecipeIngredient":
      return row.ingredient.nutritionProfile;
    case "SubRecipeRecipeIngredient":
      return row.recipe.nutritionProfile ?? null;
    default:
      throw new UnreachableCaseError(row);
  }
};

const ingredientLink = (row: IngredientRow) => {
  switch (row.__typename) {
    case "IngredientRecipeIngredient":
      return row.isOutdated
        ? routes.ingredientDetailVersion.to(row.ingredient.id, row.ingredient.versionNumber)
        : routes.ingredientDetail.to(row.ingredient.id);
    case "SubRecipeRecipeIngredient":
      return row.isOutdated
        ? routes.recipeDetailVersion.to(row.recipe.id, row.recipe.versionNumber)
        : routes.recipeDetail.to(row.recipe.id);
    default:
      throw new UnreachableCaseError(row);
  }
};

const ingredientTypeColor = (typename: IngredientRow["__typename"]) => {
  switch (typename) {
    case "IngredientRecipeIngredient":
      return "blue";
    case "SubRecipeRecipeIngredient":
      return "purple";
    default:
      throw new UnreachableCaseError(typename);
  }
};

type RecipeIngredientNameCellProps = {
  readonly record: IngredientRow;
};

export const RecipeIngredientNameCell = ({ record }: RecipeIngredientNameCellProps) => {
  const { t } = useTranslation();
  const name = ingredientName(record);
  const nameForLabel = ingredientNameForLabel(record);
  const np = ingredientNutritionProfile(record);
  const link = ingredientLink(record);
  const isDeleted = ingredientDeleted(record);

  return (
    <Flex gap={2} vertical>
      <Space wrap>
        {isDeleted ? (
          <Tooltip destroyOnHidden title={t("recipes.ingredientDeletedTooltip")}>
            <DeleteOutlined />
          </Tooltip>
        ) : null}
        <span>
          <Link className={isDeleted ? deletedName : undefined} to={link}>
            {name}
          </Link>
          {nameForLabel != null ? (
            <span>
              {" "}
              · <Typography.Text type="secondary">{nameForLabel}</Typography.Text>
            </span>
          ) : null}
        </span>
        <Tag color={ingredientTypeColor(record.__typename)}>{t(`recipes.ingredientTypes.${record.__typename}`)}</Tag>
        {record.isOutdated ? (
          <Tooltip destroyOnHidden title={t("recipes.ingredientOutdatedTooltip")}>
            <Tag color="warning" icon={<HistoryOutlined />}>
              {t("recipes.ingredientOutdated")}
            </Tag>
          </Tooltip>
        ) : null}
      </Space>
      {np ? (
        <div>
          <NutritionSummary name={name} nutritionProfile={np} showTooltip />
        </div>
      ) : null}
    </Flex>
  );
};
